import { useStartParentPersonalDataExport, useStartStudentPersonalDataExport } from '@actions';
import { ReactComponent as InfoIcon } from '@assets/icons/V4/info-icon.svg';
import Button from '@components/V4/Button';
import IconButton from '@components/V4/IconButton';
import Modal from '@components/V4/Modal/Modal';
import { useAuthState } from '@contexts/AuthContext';
import { StudentTier, STUDENT_TIER_OPTIONS } from '@shared/clientTypes/misc';
import { UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { getUserLocation } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import { StudentDetailsPageContext } from '../StudentDetailsPage';
import StudentDetailsForm from './StudentDetailsForm';

const formatEduNpsText = (
  npsData?: {
    npsAverage: number | null;
    npsLast: number | null;
    npsLastDate: Date | null;
  } | null
) => {
  if (!npsData) {
    return 'n/a';
  }

  const average = typeof npsData.npsAverage === 'number' ? npsData.npsAverage.toFixed(2) : 'n/a';
  const last = typeof npsData.npsLast === 'number' ? npsData.npsLast : 'n/a';
  const lastDate =
    npsData.npsLastDate && moment(npsData.npsLastDate).isValid()
      ? moment(npsData.npsLastDate).format('DD.MM.YYYY')
      : null;

  if (lastDate) {
    return `${average} (${last} last on ${lastDate})`;
  }

  return `${average} (${last} last)`;
};

const StudentDetailsSection = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isMajorTimelineModalOpen, setIsMajorTimelineModalOpen] = useState(false);

  const { student } = useContext(StudentDetailsPageContext);

  const mostRelevantLastActivity = useMemo(() => {
    if (student?.lastActivityTimestamp && student?.lastLogin) {
      const lastActivity = moment(student?.lastActivityTimestamp).isAfter(
        moment(student?.lastLogin)
      );
      return lastActivity ? student?.lastActivityTimestamp : student?.lastLogin;
    }

    return student?.lastLogin || student?.lastActivityTimestamp;
  }, [student?.lastActivityTimestamp, student?.lastLogin]);

  const tierStatusLabel = useMemo(() => {
    const tier = student?.tier;

    if (tier === null || typeof tier === 'undefined') {
      return undefined;
    }

    const numericTier =
      typeof tier === 'number' ? tier : StudentTier[tier as keyof typeof StudentTier];

    if (typeof numericTier === 'number') {
      return (
        STUDENT_TIER_OPTIONS.find((option) => option.value === numericTier)?.label ??
        StudentTier[numericTier]
      );
    }

    return typeof tier === 'string' ? tier : undefined;
  }, [student?.tier]);

  const majorTimelineEntries = useMemo(() => {
    if (!student?.majorHistory?.length) {
      return [];
    }

    return [...student.majorHistory].reverse();
  }, [student?.majorHistory]);

  const studentDetailsGroups: {
    title: string;
    rows: { label: string; value?: string | number; href?: string }[];
  }[] = [
    {
      title: 'Contact',
      rows: [
        { label: 'Mail:', value: student?.email },
        {
          label: 'Phone:',
          value: `${student?.phoneNumber?.countryCode ?? ''}${student?.phoneNumber?.number ?? ''}`,
        },
        { label: 'Location:', value: getUserLocation(student) },
        { label: 'Timezone:', value: student?.timezone },
        { label: 'Language:', value: student?.spokenLanguages?.join(', ') },
      ],
    },
    {
      title: 'Academic',
      rows: [
        { label: 'Graduation Year:', value: student?.graduationYear },
        { label: 'Major:', value: student?.major?.name },
        { label: 'NPS:', value: formatEduNpsText(student?.nps?.student) },
      ],
    },
    {
      title: 'Parent',
      rows: [
        { label: 'Name:', value: student?.parentData?.fullName },
        { label: 'Mail:', value: student?.parentData?.email },
        {
          label: 'Phone:',
          value: `${student?.parentData?.phoneNumber?.countryCode ?? ''}${
            student?.parentData?.phoneNumber?.number ?? ''
          }`,
        },
        { label: 'NPS:', value: formatEduNpsText(student?.nps?.parent) },
      ],
    },
    {
      title: 'Education Strategist',
      rows: [
        {
          label: 'Name:',
          value:
            student?.educationStrategist?.fullName ??
            (student?.educationStrategist
              ? `${student.educationStrategist.firstName ?? ''} ${
                  student.educationStrategist.lastName ?? ''
                }`.trim()
              : undefined),
        },
        { label: 'Mail:', value: student?.educationStrategist?.email },
        {
          label: 'Phone:',
          value: student?.educationStrategist?.phoneNumber
            ? `${student.educationStrategist.phoneNumber.countryCode ?? ''}${
                student.educationStrategist.phoneNumber.number ?? ''
              }`
            : undefined,
        },
      ],
    },
    {
      title: 'Contract',
      rows: [
        { label: 'Tier:', value: tierStatusLabel },
        {
          label: 'Start:',
          value: student?.startContractDate
            ? moment(student.startContractDate).format('DD.MM.YYYY')
            : undefined,
        },
        {
          label: 'End:',
          value: student?.endContractDate
            ? moment(student.endContractDate).format('DD.MM.YYYY')
            : undefined,
        },
      ],
    },
    {
      title: 'Account',
      rows: [
        {
          label: 'Started At:',
          value: student?.createdAt ? moment(student.createdAt).format('DD.MM.YYYY') : undefined,
        },
        {
          label: 'Last Activity:',
          value: mostRelevantLastActivity
            ? moment(mostRelevantLastActivity).format('DD.MM.YYYY; HH:mm')
            : undefined,
        },
        {
          label: 'Drive:',
          value: student?.googleDrive ? 'Open folder' : undefined,
          href: student?.googleDrive
            ? `https://drive.google.com/drive/folders/${student.googleDrive}`
            : undefined,
        },
      ],
    },
  ];

  const editModeChangeHandler = () => {
    setIsEditMode((editMode) => !editMode);
  };

  const exportToast = useToast();
  const { roles } = useAuthState();
  const isSuperUser = roles?.includes(UserRole.SuperUser);

  const exportHandlers = {
    onSuccess: () => {
      exportToast.success(
        'Personal data export started. You will receive an email with the download link.'
      );
    },
    onError: (error: any) => {
      exportToast.error(
        error?.response?.data?.error?.message ?? 'The personal data export could not be started.'
      );
    },
  };

  const { mutate: startStudentExport, isLoading: isStartingStudentExport } =
    useStartStudentPersonalDataExport(student?._id ?? '', exportHandlers);

  const { mutate: startParentExport, isLoading: isStartingParentExport } =
    useStartParentPersonalDataExport(student?._id ?? '', exportHandlers);

  return isEditMode ? (
    <StudentDetailsForm onCloseEditMode={editModeChangeHandler} />
  ) : (
    <>
      <div className="flex justify-between mb-2">
        <p className="text-xs font-bold">Student Details</p>
        <div className="flex gap-2">
          {isSuperUser && (
            <>
              <Button
                buttonText="Export student data"
                variant="secondary"
                isLoading={isStartingStudentExport}
                onClick={() => startStudentExport({})}
              />
              <Button
                buttonText="Export parent data"
                variant="secondary"
                isLoading={isStartingParentExport}
                onClick={() => startParentExport({})}
              />
            </>
          )}
          <Button buttonText="Edit" onClick={editModeChangeHandler} />
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-x-8 gap-y-5 tablet:grid-cols-2 laptop:grid-cols-3">
        {studentDetailsGroups.map((group) => (
          <div key={group.title} className="flex flex-col gap-2">
            <p className="font-raleway text-xxs font-bold uppercase tracking-wide text-customPrimary">
              {group.title}
            </p>
            {group.rows.map((studentDetail, index) => (
              <span key={`row-${index}`} className="flex text-customGrey font-raleway text-xs">
                <span className="min-w-max">{studentDetail.label}</span>
                <span className="text-white ml-2 break-words">
                  {studentDetail.href && studentDetail.value ? (
                    <a
                      href={studentDetail.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-customPrimary underline"
                    >
                      {studentDetail.value}
                    </a>
                  ) : (
                    studentDetail.value ?? 'n/a'
                  )}
                </span>
                {studentDetail.label === 'Major:' && majorTimelineEntries.length > 0 ? (
                  <IconButton
                    className="ml-1"
                    variant="ghost"
                    size="small"
                    icon={<InfoIcon className="w-4 h-4" />}
                    onClick={() => setIsMajorTimelineModalOpen(true)}
                  />
                ) : null}
              </span>
            ))}
          </div>
        ))}
      </div>

      <Modal
        isOpen={isMajorTimelineModalOpen}
        onClose={() => setIsMajorTimelineModalOpen(false)}
        size="small"
      >
        <Modal.Header title="Major History" />
        <Modal.Body className="max-h-[60vh] overflow-y-auto gap-3">
          {majorTimelineEntries.length ? (
            <div className="flex flex-col gap-2">
              {majorTimelineEntries.map((entry, index) => {
                const changedAt = entry?.changedAt
                  ? moment(entry.changedAt).format('DD.MM.YYYY')
                  : 'n/a';

                return (
                  <div
                    key={`${entry?.changedAt ?? 'unknown'}-${index}`}
                    className="border border-slate-600 rounded p-2"
                  >
                    <p className="text-white text-sm font-semibold">
                      {entry?.major?.name ?? 'n/a'}
                    </p>
                    <p className="text-customGrey text-xs">Changed on {changedAt}</p>
                    {entry?.changedBy?.fullName ? (
                      <p className="text-customGrey text-xs">By {entry.changedBy.fullName}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-customGrey text-sm">No major changes recorded.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default StudentDetailsSection;
